import React, { FunctionComponent, useEffect } from 'react';
import { DrawerLayout } from '../../Layout';
import { DatePicker, Input, Spin, Table, Tabs } from 'antd';
import { PaginationConfig, paginationStyles } from '../common';
import styled from 'styled-components';
import { SearchOutlined } from '@ant-design/icons';
import style from './style';
import { Moment } from 'moment';

const PaymentsWrapper = styled.div`
    ${style}
    ${paginationStyles}
`;

const { RangePicker } = DatePicker;

const safeNumber = (value: any) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
};

const roundToTwo = (value: any) => {
    return Math.round((safeNumber(value) + Number.EPSILON) * 100) / 100;
};

const formatAmount = (value: any) => {
    const fixedValue = roundToTwo(value).toFixed(2);
    const [integer, decimal] = fixedValue.split('.');
    return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimal}`;
};

const sumStats = (data: any[] | undefined, key: string) => {
    if (!Array.isArray(data)) {
        return 0;
    }
    return data.reduce((sum, item) => sum + safeNumber(item?.[key]), 0);
};

const formatTrendPercentage = (value: any) => {
    const percentage = Math.abs(safeNumber(value));
    const rounded = Math.round(percentage * 10) / 10;
    return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
};

const getPaymentTotals = (paymentStatsData: any) => {
    return {
        deposits: roundToTwo(sumStats(paymentStatsData?.total_deposit, 'deposits')),
        withdrawals: roundToTwo(sumStats(paymentStatsData?.total_withdrawal, 'withdrawal')),
        withdrawalCharges: roundToTwo(sumStats(paymentStatsData?.total_withdrawal_charges, 'withdrawal')),
        platformCharges: roundToTwo(sumStats(paymentStatsData?.total_platform_charges, 'withdrawal')),
    };
};

const getTrend = (currentValue: number, previousValue: number, unit?: 'week' | 'day') => {
    if (!unit) {
        return undefined;
    }

    if (previousValue === 0 && currentValue === 0) {
        return {
            label: `No change from last ${unit}`,
            status: 'neutral',
        };
    }

    if (previousValue === 0) {
        return {
            label: `New from last ${unit}`,
            status: 'up',
        };
    }

    const change = ((currentValue - previousValue) / previousValue) * 100;
    if (change === 0) {
        return {
            label: `No change from last ${unit}`,
            status: 'neutral',
        };
    }

    return {
        label: `${change > 0 ? 'Up' : 'Down'} ${formatTrendPercentage(change)} from last ${unit}`,
        status: change > 0 ? 'up' : 'down',
    };
};

const getPaymentSummaryCards = (paymentStatsData: any, comparisonStatsData: any, trendUnit?: 'week' | 'day') => {
    const totals = getPaymentTotals(paymentStatsData);
    const comparisonTotals = getPaymentTotals(comparisonStatsData);

    return [
        {
            label: 'Deposits',
            value: totals.deposits,
            trend: getTrend(totals.deposits, comparisonTotals.deposits, trendUnit),
        },
        {
            label: 'Withdrawals',
            value: totals.withdrawals,
            trend: getTrend(totals.withdrawals, comparisonTotals.withdrawals, trendUnit),
        },
        {
            label: 'Withdrawal charges',
            value: totals.withdrawalCharges,
            trend: getTrend(totals.withdrawalCharges, comparisonTotals.withdrawalCharges, trendUnit),
        },
        {
            label: 'Platform charges',
            value: totals.platformCharges,
            trend: getTrend(totals.platformCharges, comparisonTotals.platformCharges, trendUnit),
        },
    ];
};

interface IProps {
    columns: any;
    columns2: any;
    columns3: any;
    columns4: any;
    data?: MPesaTransactions[];
    loading: boolean;
    total?: number;
    fetchNextPage?: () => void;
    fetchPreviousPage?: () => void;
    isFetchingNextPage?: boolean;
    isFetchingPreviousPage?: boolean;
    setFetchSearch?: (value: string) => void;
    activeTab: string;
    setActiveTab: (value: string) => void;
    paymentStatsData?: any;
    paymentStatsComparisonData?: any;
    paymentStatsTrendUnit?: 'week' | 'day';
    isFetchingPaymentStats?: boolean;
    paymentStatsDateRange: [Moment, Moment];
    setPaymentStatsDateRange: (value: [Moment, Moment]) => void;
}
const PaymentComponent: FunctionComponent<IProps> = ({
    columns,
    columns2,
    columns3,
    columns4,
    data,
    loading,
    total,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    activeTab,
    setActiveTab,
    setFetchSearch,
    paymentStatsData,
    paymentStatsComparisonData,
    paymentStatsTrendUnit,
    isFetchingPaymentStats,
    paymentStatsDateRange,
    setPaymentStatsDateRange,
}: IProps) => {
    const [page, setPage] = React.useState<any>(1);
    const [search, setSearch] = React.useState<any>('');
    const paymentSummaryCards = React.useMemo(
        () => getPaymentSummaryCards(paymentStatsData, paymentStatsComparisonData, paymentStatsTrendUnit),
        [paymentStatsComparisonData, paymentStatsData, paymentStatsTrendUnit],
    );

    useEffect(() => {
        const timeout = setTimeout(() => {
            setPage(1);
            setFetchSearch?.(search);
        }, 500);
        return () => clearTimeout(timeout);
    }, [search]);

    return (
        <DrawerLayout>
            <PaymentsWrapper>
                <div className="payments-stats-header">
                    <div className="payments-stats-title">Payment Insights</div>
                    <RangePicker
                        allowClear={false}
                        value={paymentStatsDateRange}
                        onChange={(values) => {
                            if (values && values[0] && values[1]) {
                                setPaymentStatsDateRange([values[0], values[1]]);
                            }
                        }}
                    />
                </div>
                <div className="payments-summary-grid">
                    {paymentSummaryCards.map((item) => (
                        <div className="payments-summary-card" key={item.label}>
                            <div className="payments-summary-card-label">{item.label}</div>
                            <div className="payments-summary-card-value">KES {formatAmount(item.value)}</div>
                            {item.trend ? (
                                <div className={`payments-summary-card-trend ${item.trend.status}`}>
                                    (Trend - {item.trend.label})
                                </div>
                            ) : null}
                        </div>
                    ))}
                    {isFetchingPaymentStats ? (
                        <div className="payments-stats-loading">
                            <Spin />
                        </div>
                    ) : null}
                </div>
                <Tabs
                    defaultActiveKey="deposits"
                    type="card"
                    tabBarStyle={{ marginBottom: 0 }}
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    tabBarExtraContent={
                        <Input
                            value={search}
                            allowClear
                            placeholder={'Search Phone/receipt'}
                            prefix={<SearchOutlined />}
                            onChange={(event) => {
                                setSearch(event.target.value);
                            }}
                        />
                    }
                >
                    <Tabs.TabPane tab="Deposits" key="deposits">
                        <Table
                            loading={loading || isFetchingNextPage || isFetchingPreviousPage}
                            rowKey={'id'}
                            columns={columns}
                            dataSource={data}
                            locale={{ emptyText: 'No data' }}
                            pagination={{
                                ...PaginationConfig,
                                total: total,
                                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
                                onChange: (pageTable) => {
                                    if (pageTable > page) {
                                        fetchNextPage?.();
                                    } else if (pageTable < page) {
                                        fetchPreviousPage?.();
                                    }
                                    setPage(pageTable);
                                },
                            }}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Withdrawals" key="withdrawals">
                        <Table
                            loading={loading || isFetchingNextPage || isFetchingPreviousPage}
                            rowKey={'id'}
                            columns={columns2}
                            dataSource={data}
                            locale={{ emptyText: 'No data' }}
                            pagination={{
                                ...PaginationConfig,
                                total: total,
                                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
                                onChange: (pageTable) => {
                                    if (pageTable > page) {
                                        fetchNextPage?.();
                                    } else if (pageTable < page) {
                                        fetchPreviousPage?.();
                                    }
                                    setPage(pageTable);
                                },
                            }}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Paybill Payments" key="paybill-payments">
                        <Table
                            loading={loading || isFetchingNextPage || isFetchingPreviousPage}
                            rowKey={'id'}
                            columns={columns3}
                            dataSource={data}
                            locale={{ emptyText: 'No data' }}
                            pagination={{
                                ...PaginationConfig,
                                total: total,
                                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
                                onChange: (pageTable) => {
                                    if (pageTable > page) {
                                        fetchNextPage?.();
                                    } else if (pageTable < page) {
                                        fetchPreviousPage?.();
                                    }
                                    setPage(pageTable);
                                },
                            }}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Recent Payments" key="recent-payments">
                        <Table
                            loading={loading || isFetchingNextPage || isFetchingPreviousPage}
                            rowKey={'id'}
                            columns={columns4}
                            dataSource={data}
                            locale={{ emptyText: 'No data' }}
                            pagination={{
                                ...PaginationConfig,
                                total: total,
                                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
                                onChange: (pageTable) => {
                                    if (pageTable > page) {
                                        fetchNextPage?.();
                                    } else if (pageTable < page) {
                                        fetchPreviousPage?.();
                                    }
                                    setPage(pageTable);
                                },
                            }}
                        />
                    </Tabs.TabPane>
                </Tabs>
            </PaymentsWrapper>
        </DrawerLayout>
    );
};

export default PaymentComponent;
